import React, { useState, useEffect } from 'react';
import { proveedorService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function ProveedorManagement() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
    activo: true
  });

  useEffect(() => {
    fetchProveedores();
  }, []);

  const fetchProveedores = async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      const response = await proveedorService.getAllProveedores();
      if (response.success) {
        setProveedores(response.data || []);
      } else {
        setError(response.message || 'Error al cargar los proveedores');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar los proveedores');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorTrace(null);
    setSuccess('');

    try {
      let response;
      if (editingProveedor) {
        response = await proveedorService.updateProveedor(editingProveedor.id, formData);
      } else {
        response = await proveedorService.createProveedor(formData);
      }

      if (response.success) {
        setSuccess(editingProveedor ? 'Proveedor actualizado exitosamente' : 'Proveedor creado exitosamente');
        resetForm();
        fetchProveedores();
      } else {
        setError(response.message || 'Error al guardar el proveedor');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al guardar el proveedor');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const handleEdit = (proveedor) => {
    setEditingProveedor(proveedor);
    setFormData({
      nombre: proveedor.nombre,
      direccion: proveedor.direccion || '',
      telefono: proveedor.telefono || '',
      email: proveedor.email || '',
      activo: proveedor.activo
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este proveedor?')) {
      try {
        setError('');
        setErrorTrace(null);
        const response = await proveedorService.deleteProveedor(id);
        if (response.success) {
          setSuccess('Proveedor eliminado exitosamente');
          fetchProveedores();
        } else {
          setError(response.message || 'Error al eliminar el proveedor');
          setErrorTrace(response.errorTrace || null);
        }
      } catch (err) {
        const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al eliminar el proveedor');
        setError(message);
        setErrorTrace(trace);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      direccion: '',
      telefono: '',
      email: '',
      activo: true
    });
    setEditingProveedor(null);
    setShowForm(false);
  };

  const openNewProveedorForm = () => {
    resetForm();
    setShowForm(true);
  };

  return (
    <div className="proveedor-management">
      <div className="section-header">
        <h2>Gestión de Proveedores</h2>
        <div className="header-actions">
          <button onClick={openNewProveedorForm} className="btn btn-primary">
            + Nuevo Proveedor
          </button>
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />
      {success && <p className="success-message">{success}</p>}

      {showForm && (
        <div className="proveedor-form-container">
          <form onSubmit={handleSubmit} className="proveedor-form">
            <h3>{editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h3>
            
            <div className="input-group">
              <label htmlFor="nombre">Nombre</label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Nombre del proveedor"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="direccion">Dirección</label>
              <input
                type="text"
                id="direccion"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Dirección"
              />
            </div>

            <div className="input-group">
              <label htmlFor="telefono">Teléfono</label>
              <input
                type="text"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="Teléfono"
              />
            </div>

            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
              />
            </div>

            {editingProveedor && (
              <div className="input-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleChange}
                  />
                  Proveedor activo
                </label>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingProveedor ? 'Actualizar' : 'Crear'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Cargando proveedores...</p>
      ) : proveedores.length === 0 ? (
        <div className="empty-state">
          <p>No hay proveedores registrados</p>
        </div>
      ) : (
        <div className="proveedores-table">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Dirección</th>
                <th>Teléfono</th>
                <th>Email</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {proveedores.map((proveedor) => (
                <tr key={proveedor.id} className={!proveedor.activo ? 'inactive-row' : ''}>
                  <td>{proveedor.nombre}</td>
                  <td>{proveedor.direccion || '-'}</td>
                  <td>{proveedor.telefono || '-'}</td>
                  <td>{proveedor.email || '-'}</td>
                  <td>
                    <span className={`status-badge ${proveedor.activo ? 'status-active' : 'status-inactive'}`}>
                      {proveedor.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      onClick={() => handleEdit(proveedor)}
                      className="btn btn-small"
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => handleDelete(proveedor.id)}
                      className="btn btn-small btn-danger"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProveedorManagement;
